import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, X, ChevronDown, Zap, Users, Building2, Copy, Check, Sparkles, Brain, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { ASSESSMENT_PACKS, ASSESSMENT_ORG_PACKS, COMBO_PACKS } from "@/data/assessments";

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function CountdownTimer({ expiry }) {
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiry) - new Date();
      if (diff <= 0) { setTimeLeft("Expired"); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${d > 0 ? d + "d " : ""}${h}h ${m}m remaining`);
    };
    calc();
    const timer = setInterval(calc, 60000);
    return () => clearInterval(timer);
  }, [expiry]);
  if (timeLeft === "Expired") return null;
  return (
    <div className="text-center mb-6">
      <span className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 font-dm text-sm font-bold px-5 py-2 rounded-full">
        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        Limited Time Offer — {timeLeft}
      </span>
    </div>
  );
}

const individualPacks = [
  { name: "Starter Pack", price: 499, tokens: 10, per: "\u20B949.90/map", featured: false },
  { name: "Growth Pack", price: 999, tokens: 25, per: "\u20B939.96/map", featured: true, badge: "Most Popular" },
  { name: "Pro Pack", price: 1999, tokens: 60, per: "\u20B933.32/map", featured: false },
  { name: "Power Pack", price: 4999, tokens: 175, per: "\u20B928.57/map", featured: false },
];

// Hardcoded fallbacks for sections that previously had inline arrays
const HARDCODED_COACHING_PACKS = [
  { name: "Coaching Starter", price: 999, tokens: 60, desc: "12 coaching sessions at 30 min each" },
  { name: "Coaching Growth", price: 1999, tokens: 150, desc: "30 coaching sessions · Save 19%" },
  { name: "Coaching Pro", price: 3499, tokens: 300, desc: "60 sessions — full quarter · Save 29%" },
  { name: "Coaching Team", price: 7999, tokens: 800, desc: "160 sessions · team of 10 · Save 39%", featured: true, badge: "Best value" },
];

const HARDCODED_CAPSCAN_PACKS = [
  { name: "CapScan Solo", price: 999, tokens: 10, desc: "5 individual CapScans" },
  { name: "CapScan Team", price: 2999, tokens: 40, desc: "20 individual + 4 team scans · Save 25%" },
  { name: "CapScan Org", price: 6999, tokens: 100, desc: "50 individual scans · quarterly capability cycle · Save 30%" },
];

const HARDCODED_SMART_BUNDLES = [
  { name: "Explorer", price: 1499, intl: 10, assess: 50, tag: "Try every module once", badge: "Starter", value: 2200, save: 701 },
  { name: "Coach & Grow", price: 2499, intl: 10, assess: 150, tag: "30 coaching sessions + 5 maps", badge: "Coaching focus", value: 3499, save: 1000 },
  { name: "Professional", price: 4999, intl: 25, assess: 150, tag: "Most popular — includes onboarding call", badge: "Most popular", value: 7200, save: 2201, featured: true },
  { name: "Team Intelligence", price: 7999, intl: 50, assess: 300, tag: "Team CapScan + AXIS for 6 people", badge: "Team", value: 12400, save: 4401 },
  { name: "Workforce Readiness", price: 19999, intl: 150, assess: 1000, tag: "Full gap analysis for up to 50 employees", badge: "Organisation", value: 34000, save: 14001 },
  { name: "Enterprise", price: 49999, intl: 500, assess: 3000, tag: "Unlimited users · dedicated onboarding", badge: "Enterprise", value: 96000, save: 46001, dark: true },
];

const orgPacks = [
  { name: "Team", users: "Up to 10 users", price: 4999, tokens: 200, desc: "200 tokens shared across team" },
  { name: "Business", users: "Up to 50 users", price: 14999, tokens: 750, desc: "750 tokens shared across team" },
  { name: "Enterprise", users: "100+ users", price: 29999, tokens: 2000, desc: "2000 tokens + priority support" },
];

const compareFeatures = [
  { name: "Competency maps", free: "5 total", paid: "Per tokens purchased" },
  { name: "Excel download", free: true, paid: true },
  { name: "Job Description generation", free: true, paid: true },
  { name: "Community access", free: true, paid: true },
  { name: "Map history & saved maps", free: true, paid: true },
  { name: "She Leads access", free: "Female users", paid: "Female users" },
  { name: "Token expiry", free: "Never", paid: "Never" },
  { name: "Team sharing", free: false, paid: "Org packs only" },
  { name: "Priority support", free: false, paid: "Enterprise only" },
  { name: "API access", free: false, paid: "Enterprise only" },
];

const faqs = [
  { q: "What is a token?", a: "1 token = 1 competency map generation. Each time you generate a framework for a role, it costs 1 token." },
  { q: "Do tokens expire?", a: "No. Buy tokens, use them whenever you want. They never expire." },
  { q: "Can I share tokens with my team?", a: "Organisation packs come with shared token pools that any team member can use." },
  { q: "How do I get more tokens?", a: "Purchase any token pack from the pricing page. Tokens are credited instantly to your account." },
  { q: "Is there a free option?", a: "Yes. You can generate 2 maps without an account, and get 5 free tokens when you sign up." },
];

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-navy/10">
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center py-5 text-left">
        <span className="font-dm text-navy font-semibold">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="font-dm text-navy/70 text-sm pb-5">{a}</p>}
    </div>
  );
}

function BuyModal({ isOpen, onClose, pack }) {
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [discountCfg, setDiscountCfg] = useState({ config: {}, pack_overrides: {} });
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);  // { code, discount, applies_to }
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/api/discounts/active`).then((r) => setDiscountCfg(r.data || { config: {}, pack_overrides: {} })).catch(() => {});
      setCouponInput("");
      setCouponApplied(null);
      setCouponError("");
    }
  }, [isOpen]);

  if (!isOpen || !pack) return null;

  // Compute auto-discount (global or module — whichever is HIGHEST applicable) + pack override
  const category = pack.category || "all";
  const cfg = discountCfg.config || {};
  const globalD = cfg.global?.active && (cfg.global.applies_to === "all" || cfg.global.applies_to === category) ? Math.max(0, Math.min(60, cfg.global.discount || 0)) : 0;
  const moduleD = cfg.modules?.[category]?.active ? Math.max(0, Math.min(60, cfg.modules[category].discount || 0)) : 0;
  const packOverride = discountCfg.pack_overrides?.[pack.name] || 0;
  const autoDiscount = Math.max(globalD, moduleD, packOverride);
  const couponD = couponApplied?.discount || 0;
  // Stack auto + coupon ADDITIVELY then hard-cap at 60%
  const stackedDiscount = Math.min(60, autoDiscount + couponD);
  const finalPrice = Math.round(pack.price * (1 - stackedDiscount / 100));

  const applyCoupon = async () => {
    setCouponError("");
    setCouponLoading(true);
    try {
      const r = await axios.post(`${API}/api/coupons/validate`, { code: couponInput.trim(), pack_category: category });
      setCouponApplied({ code: couponInput.trim().toUpperCase(), discount: r.data.discount, applies_to: r.data.applies_to });
    } catch (e) {
      setCouponError(e?.response?.data?.detail || "Invalid coupon");
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const copyUPI = () => {
    navigator.clipboard.writeText("8080315440@upi");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNotify = async () => {
    // Save payment request to backend
    try {
      await axios.post(`${API}/api/payments/notify`, {
        pack_name: pack.name, amount: finalPrice, tokens: pack.tokens, email: email,
        tokens_type: pack.tokens_type || "competency",
        bundle_assessment_tokens: pack.assessmentTokens || 0,
        coupon_code: couponApplied?.code || "",
        original_amount: pack.price,
        discount_applied: stackedDiscount,
      }, { withCredentials: true });
    } catch (err) { console.error(err); }
    // Open WhatsApp with payment-specific message (so admin knows who paid and what pack)
    const discountNote = stackedDiscount > 0 ? ` (after ${stackedDiscount}% discount${couponApplied?.code ? ` · code ${couponApplied.code}` : ""})` : "";
    const msg = encodeURIComponent(`Hi RoleIQ Team, I have completed payment for ${pack.name} — \u20B9${finalPrice.toLocaleString()}${discountNote}. My registered email is ${email}. Please credit my tokens. Thank you.`);
    window.open(`https://wa.me/919136262913?text=${msg}`, "_blank");
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center" onClick={e => e.stopPropagation()}>
          <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h3 className="font-playfair text-navy text-xl font-bold mb-2">Thank You!</h3>
          <p className="font-dm text-navy/60 text-sm mb-6 leading-relaxed">Your tokens will be credited within 2 hours. You will receive a confirmation on your registered email.</p>
          <button onClick={() => { setSubmitted(false); setEmail(""); onClose(); }} className="bg-navy text-white font-dm font-bold px-8 py-3 rounded-lg hover:bg-navy/90 transition-colors">Done</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-playfair text-navy text-xl font-bold">Complete Your Payment</h3>
            <p className="font-dm text-navy/50 text-sm">{pack.name} — {pack.tokens} Tokens</p>
          </div>
          <button onClick={onClose} className="text-navy/30 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="bg-cream rounded-xl p-4 mb-4 text-center">
          <p className="font-dm text-navy/60 text-xs uppercase tracking-wider mb-1">Amount to Pay</p>
          {stackedDiscount > 0 ? (
            <>
              <p className="font-dm text-navy/40 text-sm line-through">{"\u20B9"}{pack.price.toLocaleString()}</p>
              <p className="font-playfair text-gold text-3xl font-bold">{"\u20B9"}{finalPrice.toLocaleString()}</p>
              <p className="font-dm text-green-700 text-xs font-bold mt-1">You save {"\u20B9"}{(pack.price - finalPrice).toLocaleString()} · {stackedDiscount}% off</p>
            </>
          ) : (
            <p className="font-playfair text-gold text-3xl font-bold">{"\u20B9"}{pack.price.toLocaleString()}</p>
          )}
        </div>

        {/* Coupon Code */}
        <div className="mb-4 bg-navy/5 rounded-lg p-3" data-testid="coupon-section">
          {!couponApplied ? (
            <>
              <label className="font-dm text-navy/70 text-xs font-semibold block mb-1">Have a promo code?</label>
              <div className="flex gap-2">
                <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="coupon-code-input"
                  className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-xs font-mono uppercase focus:border-gold focus:outline-none" />
                <button onClick={applyCoupon} disabled={!couponInput || couponLoading} data-testid="apply-coupon-btn"
                  className="bg-navy text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg disabled:opacity-50">
                  {couponLoading ? "..." : "Apply"}
                </button>
              </div>
              {couponError && <p className="font-dm text-red-600 text-[11px] mt-2" data-testid="coupon-error">{couponError}</p>}
              {autoDiscount > 0 && (
                <p className="font-dm text-green-700 text-[11px] mt-2 font-semibold">✓ Auto-discount applied: {autoDiscount}% off (stacks with promo code, max 60%)</p>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between">
              <p className="font-dm text-green-700 text-xs font-bold" data-testid="coupon-applied">✓ {couponApplied.code} applied — {couponApplied.discount}% off{autoDiscount > 0 ? ` (stacked with ${autoDiscount}% auto-discount, capped at 60%)` : ""}</p>
              <button onClick={() => { setCouponApplied(null); setCouponInput(""); }} className="text-red-500 hover:text-red-700 text-xs"><X className="w-3 h-3" /></button>
            </div>
          )}
        </div>

        {/* QR Code */}
        <div className="flex justify-center mb-3">
          <img src="/upi-qr.png" alt="UPI QR Code" className="w-[200px] h-[200px] rounded-lg" data-testid="upi-qr-code" />
        </div>
        <p className="font-dm text-navy/50 text-xs text-center mb-4">Scan with any UPI app — PhonePe, GPay, Paytm, or any bank app</p>

        {/* UPI ID */}
        <div className="flex items-center gap-2 bg-navy/5 rounded-lg p-3 mb-4">
          <span className="font-dm text-navy text-sm flex-1 font-mono">8080315440@upi</span>
          <button onClick={copyUPI} className="flex items-center gap-1 text-gold hover:text-gold/80 transition-colors" data-testid="copy-upi-btn">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="font-dm text-xs">{copied ? "Copied!" : "Copy"}</span>
          </button>
        </div>

        {/* Email input */}
        <div className="mb-4">
          <label className="font-dm text-navy text-sm font-semibold block mb-1">Your registered email address</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter the email you used to sign up" data-testid="payment-email"
            className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none" />
        </div>

        {/* Notify button */}
        <button onClick={handleNotify} data-testid="notify-payment-btn"
          className="w-full bg-gold text-navy font-dm font-bold py-3 rounded-lg hover:bg-gold/90 transition-colors mb-3">
          I Have Paid — Notify Team
        </button>
        <button onClick={onClose} className="w-full font-dm text-navy/40 text-sm hover:text-navy py-2">Cancel</button>
      </motion.div>
    </div>
  );
}

export default function PricingPage() {
  const [buyModal, setBuyModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [offer, setOffer] = useState(null);
  const [assessmentOffer, setAssessmentOffer] = useState(null);
  const [pricingCfg, setPricingCfg] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/api/offers/active`).then(r => { if (r.data.active) setOffer(r.data); }).catch(() => {});
    axios.get(`${API}/api/offers/active-assessment`).then(r => { if (r.data.active) setAssessmentOffer(r.data); }).catch(() => {});
    axios.get(`${API}/api/pricing/active`).then(r => setPricingCfg(r.data)).catch(() => setPricingCfg(null));
  }, []);

  // ── DB-driven config with hardcoded fallbacks (silent fallback if pricingCfg null or empty arrays) ──
  const cfg = pricingCfg || {};
  const useOrFallback = (dbVal, fallback) => (Array.isArray(dbVal) && dbVal.length > 0 ? dbVal : fallback);
  const display = cfg.display || { sections: {}, banner: {}, featured_bundle_name: "" };
  const sections = display.sections || {};
  const banner = display.banner || {};
  const featuredBundleName = (display.featured_bundle_name || "").toLowerCase();
  const showSec = (key) => sections[key] !== false; // default visible

  const dbIndividual = useOrFallback(cfg.individual_packs, individualPacks);
  const dbAssessment = useOrFallback(cfg.assessment_packs, ASSESSMENT_PACKS);
  const dbAssessmentOrg = useOrFallback(cfg.assessment_org_packs, ASSESSMENT_ORG_PACKS);
  const dbCombo = useOrFallback(cfg.combo_packs, COMBO_PACKS);
  const dbCoaching = useOrFallback(cfg.coaching_packs, HARDCODED_COACHING_PACKS);
  const dbCapscan = useOrFallback(cfg.capscan_packs, HARDCODED_CAPSCAN_PACKS);
  const dbBundlesRaw = useOrFallback(cfg.smart_bundles, HARDCODED_SMART_BUNDLES);
  // Apply "Most Popular" featured-bundle override
  const dbBundles = featuredBundleName
    ? dbBundlesRaw.map(b => ({ ...b, featured: b.name.toLowerCase() === featuredBundleName }))
    : dbBundlesRaw;

  // ── She Leads discount overlay (only for gender_verified female users) ──
  const sheleads = cfg.sheleads_discount || { discount: 30, active: true };
  const sheLeadsActive = sheleads.active && user?.gender_verified && (user?.gender || "").toLowerCase() === "female";
  const applySheLeads = (p) => {
    if (!sheLeadsActive) return p;
    const discounted = Math.round(p.price * (1 - sheleads.discount / 100));
    return { ...p, price: discounted, _original_price: p.price, _sheleads_discount: sheleads.discount };
  };

  const getDiscountedPrice = (pack) => {
    if (!offer || !offer.active) return null;
    if (offer.packs.length > 0 && !offer.packs.includes(pack.name)) return null;
    return Math.round(pack.price * (1 - offer.discount / 100));
  };

  const getAssessmentDiscountedPrice = (pack) => {
    if (!assessmentOffer || !assessmentOffer.active) return null;
    if (assessmentOffer.packs.length > 0 && !assessmentOffer.packs.includes(pack.name)) return null;
    return Math.round(pack.price * (1 - assessmentOffer.discount / 100));
  };

  const handleBuy = (pack) => {
    if (!user) { window.location.href = "/signup"; return; }
    setSelectedPack(pack); setBuyModal(true);
  };

  // Banner colour map
  const bannerBg = { navy: "bg-navy text-gold", gold: "bg-gold text-navy", green: "bg-green-600 text-white" }[banner.colour] || "bg-navy text-gold";

  return (
    <div>
      {/* Optional announcement banner */}
      {banner.active && banner.text && (
        <div className={`${bannerBg} text-center py-2 px-4 font-dm text-sm font-semibold`} data-testid="pricing-banner">
          {banner.text}
        </div>
      )}
      {/* She Leads overlay banner */}
      {sheLeadsActive && (
        <div className="bg-gradient-to-r from-pink-50 to-amber-50 border-b border-amber-200 text-center py-2 px-4 font-dm text-navy text-sm" data-testid="sheleads-banner">
          ✨ <strong>She Leads {sheleads.discount}% discount applied</strong> across all packs — exclusive to gender-verified members.
        </div>
      )}
      {/* Hero */}
      <section className="bg-cream pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-navy text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Simple Token Pricing
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-dm text-navy/60 text-lg mb-3">
            Buy tokens. Use them whenever you want. They never expire.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-dm text-gold font-semibold">
            1 token = 1 competency map generation
          </motion.p>
        </div>
      </section>

      {/* Free Tier */}
      <section className="bg-cream pb-10">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-md p-6 flex flex-col sm:flex-row items-center gap-4 border border-gold/20">
            <Zap className="w-10 h-10 text-gold flex-shrink-0" />
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-dm text-navy font-bold">Start Free — No Payment Required</h3>
              <p className="font-dm text-navy/60 text-sm">2 free maps without signup. 5 free tokens when you create an account.</p>
            </div>
            <Link to="/generate" className="bg-navy text-white font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-navy/90 transition-colors flex-shrink-0">Try Free</Link>
          </div>
        </div>
      </section>

      {/* Individual Packs */}
      {showSec("individual") && (
      <section className="bg-cream pb-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl text-center font-semibold mb-8">Individual Token Packs</h2>
          {offer && offer.expiry && <CountdownTimer expiry={offer.expiry} />}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {dbIndividual.map((rawP, i) => {
              const p = applySheLeads(rawP);
              const discounted = getDiscountedPrice(p);
              return (
              <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`rounded-xl p-6 relative ${p.featured ? "bg-navy border-2 border-gold shadow-[0_0_30px_rgba(201,168,76,0.15)] scale-105 z-10" : "bg-white border border-navy/10 shadow-md"}`}>
                {discounted && <span className="absolute -top-3 right-3 bg-red-500 text-white font-dm text-[10px] font-bold px-3 py-1 rounded-full">Limited Time Offer</span>}
                {p.badge && !discounted && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy font-dm text-xs font-bold px-4 py-1 rounded-full">{p.badge}</span>}
                <h3 className={`font-dm text-lg font-bold mb-1 ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
                {discounted ? (
                  <div className="mb-0.5">
                    <span className={`font-playfair text-lg line-through ${p.featured ? "text-white/40" : "text-navy/30"}`}>{"\u20B9"}{p.price.toLocaleString()}</span>
                    <p className="font-playfair text-3xl font-bold text-gold">{"\u20B9"}{discounted.toLocaleString()}</p>
                  </div>
                ) : (
                  <p className={`font-playfair text-3xl font-bold mb-0.5 text-gold`}>{"\u20B9"}{p.price.toLocaleString()}</p>
                )}
                <p className={`font-dm text-sm mb-1 ${p.featured ? "text-white/60" : "text-navy/50"}`}>{p.tokens} tokens</p>
                <p className={`font-dm text-xs mb-5 ${p.featured ? "text-gold/70" : "text-gold/80"}`}>{p.per}</p>
                <button onClick={() => handleBuy({...p, price: discounted || p.price, category: "intelligence"})} data-testid={`buy-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`w-full py-2.5 rounded-lg font-dm font-bold text-sm transition-all ${p.featured ? "bg-gold text-navy hover:bg-gold/90" : "bg-navy text-white hover:bg-navy/90"}`}>
                  Buy Tokens
                </button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* Org Packs */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl text-center font-semibold mb-3">Organisation Packs</h2>
          <p className="font-dm text-navy/50 text-center text-sm mb-10">Shared token pools for teams and enterprises</p>
          <div className="grid md:grid-cols-3 gap-6">
            {orgPacks.map((p, i) => (
              <motion.div key={p.name || p.id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-cream rounded-xl p-6 border border-navy/5">
                <div className="flex items-center gap-2 mb-3">
                  {i === 0 ? <Users className="w-5 h-5 text-gold" /> : i === 1 ? <Building2 className="w-5 h-5 text-gold" /> : <Building2 className="w-6 h-6 text-gold" />}
                  <h3 className="font-dm text-navy text-lg font-bold">{p.name}</h3>
                </div>
                <p className="font-dm text-navy/50 text-sm mb-2">{p.users}</p>
                <p className="font-playfair text-gold text-2xl font-bold mb-1">{"\u20B9"}{p.price.toLocaleString()}</p>
                <p className="font-dm text-navy/60 text-sm mb-5">{p.desc}</p>
                <button onClick={() => handleBuy({...p, category: "intelligence"})} className="w-full bg-navy text-white py-2.5 rounded-lg font-dm font-bold text-sm hover:bg-navy/90 transition-colors">
                  Buy Tokens
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CapScan + Coaching cost rows (informational) */}
      <section className="bg-white py-12 border-t border-navy/5">
        <div className="max-w-3xl mx-auto px-4 space-y-10">
          <div>
            <h2 className="font-playfair text-navy text-2xl font-semibold text-center mb-1">4D CapScan™ Costs</h2>
            <p className="font-dm text-navy/50 text-center text-xs mb-5">Uses intelligence tokens</p>
            <div className="bg-cream rounded-xl divide-y divide-navy/10 border-l-4 border-gold" data-testid="capscan-cost-section">
              {[
                ["Individual CapScan", `${cfg.action_costs?.capscan_individual ?? 2} intelligence tokens`],
                ["Team CapScan (up to 10 people)", `${cfg.action_costs?.capscan_team_small ?? 5} intelligence tokens`],
                ["Team CapScan (11–20 people)", `${cfg.action_costs?.capscan_team_large ?? 8} intelligence tokens`],
                ["Re-scan (same person, within 90 days)", `${cfg.action_costs?.capscan_rescan ?? 1} intelligence token`],
              ].map(([l, c]) => (
                <div key={l} className="flex justify-between p-4"><span className="font-dm text-navy text-sm">{l}</span><span className="font-dm text-gold font-bold text-sm">{c}</span></div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-playfair text-navy text-2xl font-semibold text-center mb-1">AI Coaching Costs</h2>
            <p className="font-dm text-navy/50 text-center text-xs mb-5">Uses assessment tokens · First session free</p>
            <div className="bg-cream rounded-xl divide-y divide-navy/10 border-l-4 border-gold" data-testid="coaching-cost-section">
              <div className="flex justify-between p-4"><span className="font-dm text-navy text-sm">First session</span><span className="bg-green-100 text-green-700 font-dm font-bold text-xs px-3 py-1 rounded-full">FREE (one per account)</span></div>
              {[
                ["15-minute session", `${cfg.action_costs?.coaching_15 ?? 3} assessment tokens`],
                ["30-minute session", `${cfg.action_costs?.coaching_30 ?? 5} assessment tokens`],
                ["45-minute session", `${cfg.action_costs?.coaching_45 ?? 7} assessment tokens`],
                ["60-minute session", `${cfg.action_costs?.coaching_60 ?? 10} assessment tokens`],
              ].map(([l, c]) => (
                <div key={l} className="flex justify-between p-4"><span className="font-dm text-navy text-sm">{l}</span><span className="font-dm text-gold font-bold text-sm">{c}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Token Packs */}
      {showSec("assessment") && (
      <section id="assessment-packs" className="bg-cream py-16 border-t border-navy/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-3 flex items-center justify-center gap-2">
            <Brain className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-navy text-2xl font-semibold">Assessment Token Packs</h2>
          </div>
          <p className="font-dm text-navy/60 text-center text-sm mb-3">Separate from competency map tokens. Use for AXIS, Tree Analysis, and all upcoming RoleIQ assessments.</p>
          <p className="font-dm text-gold text-center text-xs font-semibold mb-8">1 assessment token = ₹17.98 at Starter rate</p>
          {assessmentOffer && assessmentOffer.expiry && <CountdownTimer expiry={assessmentOffer.expiry} />}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {dbAssessment.map((rawP, i) => {
              const p = applySheLeads(rawP);
              const disc = getAssessmentDiscountedPrice(p);
              return (
                <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className={`rounded-xl p-6 relative ${p.featured ? "bg-navy border-2 border-gold shadow-[0_0_30px_rgba(201,168,76,0.15)] scale-105 z-10" : "bg-white border border-navy/10 shadow-md"}`}
                  data-testid={`assessment-pack-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                  {disc && <span className="absolute -top-3 right-3 bg-red-500 text-white font-dm text-[10px] font-bold px-3 py-1 rounded-full">Limited Time Offer</span>}
                  {p.badge && !disc && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy font-dm text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">{p.badge}</span>}
                  <h3 className={`font-dm text-base font-bold mb-1 ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
                  {disc ? (
                    <div className="mb-0.5">
                      <span className={`font-playfair text-lg line-through ${p.featured ? "text-white/40" : "text-navy/30"}`}>₹{p.price.toLocaleString()}</span>
                      <p className="font-playfair text-3xl font-bold text-gold">₹{disc.toLocaleString()}</p>
                    </div>
                  ) : (
                    <p className="font-playfair text-3xl font-bold mb-0.5 text-gold">₹{p.price.toLocaleString()}</p>
                  )}
                  <p className={`font-dm text-sm mb-1 ${p.featured ? "text-white/60" : "text-navy/50"}`}>{p.tokens} assessment tokens</p>
                  <p className={`font-dm text-xs mb-5 ${p.featured ? "text-gold/70" : "text-gold/80"}`}>{p.per}</p>
                  <button onClick={() => handleBuy({ ...p, price: disc || p.price, tokens_type: "assessment", category: "assessment" })}
                    data-testid={`buy-assessment-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                    className={`w-full py-2.5 rounded-lg font-dm font-bold text-sm transition-all ${p.featured ? "bg-gold text-navy hover:bg-gold/90" : "bg-navy text-white hover:bg-navy/90"}`}>
                    Buy Assessment Tokens
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Org Assessment Packs */}
          <div className="grid md:grid-cols-3 gap-6">
            {dbAssessmentOrg.map((rawP, i) => {
              const p = applySheLeads(rawP);
              return (
              <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-6 border border-navy/10 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-gold" />
                  <h3 className="font-dm text-navy text-lg font-bold">{p.name}</h3>
                </div>
                <p className="font-dm text-navy/50 text-sm mb-2">{p.users}</p>
                <p className="font-playfair text-gold text-2xl font-bold mb-1">₹{p.price.toLocaleString()}</p>
                <p className="font-dm text-navy/60 text-sm mb-1">{p.tokens.toLocaleString()} assessment tokens shared</p>
                <p className="font-dm text-navy/50 text-xs mb-5">{p.desc}</p>
                <button onClick={() => handleBuy({ ...p, tokens_type: "assessment", category: "assessment" })} className="w-full bg-navy text-white py-2.5 rounded-lg font-dm font-bold text-sm hover:bg-navy/90 transition-colors">
                  Buy Assessment Tokens
                </button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* Combination Packages */}
      {showSec("bundles") && (
      <section id="bundles" className="bg-white py-16 border-t border-navy/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-3 flex items-center justify-center gap-2">
            <Package className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-navy text-2xl font-semibold">Bundle Packages — Maps + Assessments Together</h2>
          </div>
          <p className="font-dm text-navy/60 text-center text-sm mb-10">The most powerful HR teams use competency intelligence AND behavioural assessments together. Save more when you combine.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {dbCombo.map((rawP, i) => {
              const p = applySheLeads(rawP);
              return (
              <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`rounded-xl p-6 relative ${p.featured ? "bg-navy border-2 border-gold text-white" : "bg-cream border border-navy/10"}`}
                data-testid={`combo-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                {p.badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 font-dm text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap ${p.featured ? "bg-gold text-navy" : "bg-navy text-gold"}`}>{p.badge}</span>}
                <h3 className={`font-playfair text-xl font-bold mb-2 ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
                <p className={`font-playfair text-3xl font-bold mb-1 ${p.featured ? "text-gold" : "text-gold"}`}>₹{p.price.toLocaleString()}</p>
                {p.save && <p className={`font-dm text-xs font-semibold mb-3 ${p.featured ? "text-green-300" : "text-green-600"}`}>{p.save}</p>}
                <div className={`space-y-1.5 mb-5 ${p.featured ? "text-white/80" : "text-navy/70"}`}>
                  <p className="font-dm text-sm flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-gold flex-shrink-0" />{p.tokens} competency map tokens
                  </p>
                  <p className="font-dm text-sm flex items-center gap-2">
                    <Brain className="w-3.5 h-3.5 text-gold flex-shrink-0" />{(p.assessmentTokens || 0).toLocaleString()} assessment tokens
                  </p>
                </div>
                <p className={`font-dm text-xs mb-5 ${p.featured ? "text-white/60" : "text-navy/50"}`}>{p.desc}</p>
                <button onClick={() => handleBuy({ ...p, tokens_type: "bundle", category: "bundles" })}
                  className={`w-full py-2.5 rounded-lg font-dm font-bold text-sm transition-all ${p.featured ? "bg-gold text-navy hover:bg-gold/90" : "bg-navy text-white hover:bg-navy/90"}`}>
                  Buy Bundle
                </button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* Coaching Packs */}
      {showSec("coaching") && (
      <section className="bg-white py-16 border-t border-navy/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl font-semibold text-center mb-2">Coaching Packs</h2>
          <p className="font-dm text-navy/60 text-center text-sm mb-8">Booster packs for AI Coaching sessions. Tokens deduct per session.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {dbCoaching.map((rawP, i) => {
              const p = applySheLeads(rawP);
              return (
              <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`relative rounded-xl p-6 border-t-4 border-gold ${p.featured ? "bg-navy text-white" : "bg-cream"}`}
                data-testid={`coaching-pack-${p.name.replace(/\s/g, "-").toLowerCase()}`}>
                {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy font-dm text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{p.badge}</span>}
                <h3 className={`font-dm font-bold mb-1 ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
                <p className="font-playfair text-3xl font-bold text-gold mb-1">₹{p.price.toLocaleString()}</p>
                <p className={`font-dm text-sm mb-1 ${p.featured ? "text-white/60" : "text-navy/50"}`}>{p.tokens} assessment tokens</p>
                <p className={`font-dm text-xs mb-5 ${p.featured ? "text-white/70" : "text-navy/60"}`}>{p.desc}</p>
                <button onClick={() => handleBuy({ ...p, tokens_type: "assessment", category: "coaching" })} className={`w-full py-2.5 rounded-lg font-dm font-bold text-sm ${p.featured ? "bg-gold text-navy" : "bg-navy text-white"}`}>Get Pack</button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* CapScan Add-ons */}
      {showSec("capscan") && (
      <section className="bg-cream py-16 border-t border-navy/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl font-semibold text-center mb-2">CapScan Add-ons</h2>
          <p className="font-dm text-navy/60 text-center text-sm mb-8">Intelligence token packs designed for 4D CapScan usage.</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {dbCapscan.map((rawP, i) => {
              const p = applySheLeads(rawP);
              return (
              <motion.div key={rawP.name || rawP._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="relative bg-white rounded-xl p-6 border-t-4 border-gold" data-testid={`capscan-pack-${p.name.replace(/\s/g, "-").toLowerCase()}`}>
                <span className="absolute -top-3 right-3 bg-green-500 text-white font-dm text-[10px] font-bold px-2.5 py-1 rounded-full">NEW</span>
                <h3 className="font-dm text-navy font-bold mb-1">{p.name}</h3>
                <p className="font-playfair text-3xl font-bold text-gold mb-1">₹{p.price.toLocaleString()}</p>
                <p className="font-dm text-navy/50 text-sm mb-1">{p.tokens} intelligence tokens</p>
                <p className="font-dm text-navy/60 text-xs mb-5">{p.desc}</p>
                <button onClick={() => handleBuy({ ...p, tokens_type: "competency", category: "capscan" })} className="w-full bg-navy text-white py-2.5 rounded-lg font-dm font-bold text-sm">Get Pack</button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* Smart Bundles */}
      {showSec("bundles") && (
      <section className="bg-white py-16 border-t border-navy/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl font-semibold text-center mb-2">Smart Bundles — Everything in One</h2>
          <p className="font-dm text-navy/60 text-center text-sm mb-10">Combine intelligence and assessment tokens. Use across any RoleIQ module.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {dbBundles.map((rawB, i) => {
              const b = applySheLeads(rawB);
              return (
              <motion.div key={rawB.name || rawB._id || i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`relative rounded-xl p-6 ${b.dark ? "bg-navy text-white" : b.featured ? "bg-navy text-white border-2 border-gold scale-105" : "bg-cream border border-navy/10"}`}
                data-testid={`smart-bundle-${b.name.replace(/\s|&/g, "-").toLowerCase()}`}>
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 font-dm text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${b.featured ? "bg-gold text-navy" : b.dark ? "bg-gold text-navy" : "bg-navy text-gold"}`}>{b.badge}</span>
                <h3 className={`font-playfair text-xl font-bold mt-3 mb-1 ${b.featured || b.dark ? "text-white" : "text-navy"}`}>{b.name}</h3>
                <p className="font-playfair text-3xl font-bold text-gold mb-2">₹{b.price.toLocaleString()}</p>
                <div className="flex gap-2 flex-wrap mb-3">
                  <span className="bg-teal-100 text-teal-700 font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">{b.intl} intelligence</span>
                  <span className="bg-amber-100 text-amber-700 font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">{b.assess} assessment</span>
                </div>
                <p className={`font-dm text-xs mb-3 ${b.featured || b.dark ? "text-white/70" : "text-navy/60"}`}>{b.tag}</p>
                <p className={`font-dm text-[10px] ${b.featured || b.dark ? "text-white/50" : "text-navy/40"}`}>Worth ₹{b.value.toLocaleString()} bought separately</p>
                <p className="inline-block bg-green-100 text-green-700 font-dm text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 mb-4">Save ₹{b.save.toLocaleString()}</p>
                <button onClick={() => handleBuy({ ...b, tokens: b.intl, assessmentTokens: b.assess, tokens_type: "bundle", category: "bundles" })} className={`w-full py-2.5 rounded-lg font-dm font-bold text-sm ${b.featured || b.dark ? "bg-gold text-navy" : "bg-navy text-white"}`}>Get Bundle</button>
              </motion.div>
            );})}
          </div>
        </div>
      </section>
      )}

      {/* Compare */}
      <section className="bg-cream py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl text-center font-semibold mb-10">What's Included</h2>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-navy/5"><th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Feature</th><th className="text-center font-dm text-navy/60 text-xs py-3 px-4">Free Account</th><th className="text-center font-dm text-gold text-xs py-3 px-4 font-bold">Token Packs</th></tr></thead>
              <tbody>
                {compareFeatures.map((f, i) => (
                  <tr key={f.name || i} className="border-t border-navy/5">
                    <td className="font-dm text-navy/80 text-sm py-3 px-4">{f.name}</td>
                    {["free", "paid"].map(col => {
                      const val = f[col];
                      return <td key={col} className="text-center py-3 px-4">
                        {val === true ? <CheckCircle className="w-4 h-4 text-gold mx-auto" /> :
                         val === false ? <X className="w-4 h-4 text-navy/20 mx-auto" /> :
                         <span className="font-dm text-navy/70 text-xs">{val}</span>}
                      </td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl text-center font-semibold mb-10">Frequently Asked Questions</h2>
          {faqs.map((f, i) => <FAQ key={f.q || i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-gold text-3xl font-bold mb-6">Start free today. Buy tokens when you need more.</h2>
          <Link to="/generate" className="inline-block bg-gold text-navy font-dm font-bold px-10 py-4 rounded-lg hover:bg-gold/90 transition-colors">Generate Your First Map Free</Link>
        </div>
      </section>

      <BuyModal isOpen={buyModal} onClose={() => setBuyModal(false)} pack={selectedPack} />
    </div>
  );
}
